package com.crowpay.views.screens.contractor

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.JumpTo
import com.crowpay.utils.SignalingSet
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.pushChanges
import com.crowpay.utils.query
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.*
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.GenericDialog
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.minus
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atStart
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.serialization.lensPath
import kotlinx.coroutines.launch

interface ClientsScreen

class ClientForm(
    client: Writable<Client>,
    parent: Validator? = null,
    val onClose: suspend (Client, saved: Boolean) -> Unit = { _, _ -> }
) : Page, Validator(parent) {
    constructor(client: Client, onClose: suspend (Client, Boolean) -> Unit) : this(Property(client), null, onClose)

    val draft = Draft(client)

    override fun ViewWriter.render2(): ViewModifiable = col {
        subTitle("Contact Info")

        rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            expanding - requiredField("Name") {
                val name = draft
                    .lensPath { it.name }
                    .validate { it.isNotBlank() }

                textInput {
                    hint = "Name"
                    content bind name
                }
            }
            expanding - requiredField("Email") {
                val email = draft
                    .lensPath { it.email }
                    .validate { it.matches(SharedUtils.emailPattern) }

                textInput {
                    hint = "Email"
                    content bind email
                }
            }
        }
        rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            expanding - requiredField("Phone Number") {
                val phone = draft
                    .lensPath { it.phoneNumber }
                    .validate { it.length == 10 }

                phoneNumberInput {
                    hint = "Phone Number"
                    content bind phone
                }
            }
            expanding - space()
        }

        subTitle("Address")

        addressForm(draft.lensPath { it.address }, this@ClientForm)

        row {
            spacing = AppDimensions.buttonRowSpacing

            expanding - space()

            deleteButton - button {
                if (viewIsNarrow) body("Delete")
                else specCenteredText("Delete")
                onClick {
                    val c = draft.await()
                    val projects = notNullSession().nonCached.project.query(
                        condition { it.client eq c._id },
                        limit = 1
                    )

                    if (projects.isEmpty())
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Delete Client?",
                                message = "Deleting a client cannot be undone. Are you sure you want to delete this client?",
                                confirmationText = "Delete Client",
                                confirmationTheme = WarningButtonSemantic()
                            ) { confirmed ->
                                if (confirmed) notNullSession().clients[c._id].delete()
                            }
                        )
                    else
                        dialogScreenNavigator.navigate(
                            GenericDialog("This client is a part of existing projects and cannot be deleted.")
                        )
                }
            }

            tertiaryButton - button {
                specCenteredText("Cancel")
                onClick {
                    draft.cancel()
                    onClose(draft(), false)
                }
            }

            tertiaryButton - button {
                ::enabled { allValid() }
                specCenteredText("Save")
                onClick("Save") {
                    val updated = draft.pushChanges(notNullSession().clients)
                    onClose(updated, true)
                }
            }
        }
    }
}

@Routable("/contractor/clients")
class ContractorClients() : ContractorPage, ClientsScreen {

    override val title: Readable<String> = Constant("Clients")

    val clients: Readable<List<Client>> = shared {
        val c = notNullContractor()
        notNullSession().clients.query(
            Query(condition { it.contractor eq c }, orderBy = sort { it.name.ascending() })
        )()
    }

    val editingClients = SignalingSet<UUID>()

    private val formCache = HashMap<UUID, ClientForm>()
    private val clientsToForms = shared {
        val clients = clients()
        val editing = editingClients()
        clients.map { client ->
            val form =
                if (client._id in editing) formCache.getOrPut(client._id) {
                    ClientForm(client) { it, _ -> editingClients.remove(it._id) }
                }
                else null

            client to form
        }
    }

    private val jumpToClient = JumpTo.TargetCategory<UUID>("Client")

    override fun ViewWriter.renderMainContent() {
        col {
            spacing = AppDimensions.majorColumnSpacing

            title("Clients")

            atStart - primaryButton - button {
                existsDefaultFalse { selectedContractor() != null }
                specCenteredText("+ Client")
                onClick {
                    notNullSession().clients.insert(
                        Client(
                            contractor = notNullContractor.await(),
                            name = "",
                            email = "",
                            phoneNumber = "",
                            address = Address()
                        ).also {
                            jumpToClient(it._id)
                            editingClients.add(it._id)
                        }
                    )
                }
            }

            centered - subTitle {
                ::exists{
                    clients().isEmpty()
                }
                content = "You have no Clients yet. Create a new Client."
            }

            expanding - recyclerView {
                children(
                    clientsToForms,
                    id = { it.first._id }
                ) { clientAndForm ->
                    sizeConstraints(maxWidth = AppDimensions.pageWidth) - outlined - col card@{
                        launch {
                            jumpToClient.newTarget(clientAndForm().first._id, this@card)
                        }
                        onlyWhen { !editingClients().contains(clientAndForm().first._id) } - row {
                            expanding - col {
                                keyString("Client Name") { clientAndForm().first.name }
                                keyString("Email") { clientAndForm().first.email }
                                keyString("Phone") { PhoneNumberFormat.USA.format(clientAndForm().first.phoneNumber.filter { it.isDigit() }.takeLast(10)) }
                                keyString("Address") { clientAndForm().first.address.toString() }
                            }

                            gravity(Align.End, Align.End) - tertiaryButton - button {
                                icon(Icon.edit, "Edit Client")
                                onClick {
                                    editingClients.add(clientAndForm().first._id)
                                }
                            }
                        }
                        onlyWhen { editingClients().contains(clientAndForm().first._id) } - stack {
                            reactive {
                                clearChildren()
                                clientAndForm().second?.render(this@stack)
                            }
                        }
                    }
                }
            }
        }
    }
}